"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Eye, Zap, Crown, BookOpen, Telescope, Sparkles } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function FixedStarsPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-yellow-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-yellow-500">/</span>
                <span className="text-sm">Sabit Yıldızlar</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Sabit Yıldızlar</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Star className="h-10 w-10 text-yellow-400" />
                Sabit Yıldızlar (Fixed Stars)
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Astrolojinin en eski dallarından biri olan sabit yıldız sistemi, gökyüzündeki
                parlak yıldızların natal haritadaki gezegen ve açılarla conjunction yaptığı noktalarda
                özel anlamlar ve güçlü etkiler yaratır. Antik çağlardan modern zamana kadar
                süren zengin bir gelenek ve bilgi birikimi içerir.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavramlar</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-yellow-400" />
                      "Sabit" Yıldızlar
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        "Sabit yıldız" terimi antik çağlardan kalma olup,
                        gezegenlerin aksine göreli pozisyonlarının sabit kaldığı yıldızları ifade eder.
                      </p>
                      <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-200 mb-2">Modern Gerçeklik:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• Yıldızlar aslında hareket eder (proper motion)</div>
                          <div>• Precession: Yaklaşık 72 yılda 1° ilerleme</div>
                          <div>• 2000 yılında güncellenmiş pozisyonlar kullanılır</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Eye className="h-5 w-5 text-amber-400" />
                      Stellar Magnitude ve Etki
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Yıldızın parlaklığı (magnitude) astrolojik etkisinin gücünü belirler.
                        1. magnitude yıldızlar en güçlü etkiye sahiptir.
                      </p>
                      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-200 mb-2">Magnitude Skalası:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• 1. Magnitude: En güçlü etki (2-3° orb)</div>
                          <div>• 2-3. Magnitude: Orta etki (1-2° orb)</div>
                          <div>• 4+ Magnitude: Zayıf etki (30' orb)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Background */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Antik Dönem (MÖ 3000-MS 500)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Mezopotamya, Mısır ve Yunan medeniyetlerinde yıldız gözlemi
                      ve mitolojik anlamları üzerine kapsamlı çalışmalar yapılmıştır.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Babil astronomları:</strong> İlk yıldız katalogları</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Hipparchus (MÖ 2. yy):</strong> Magnitude sistemi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Ptolemy:</strong> 48 takımyıldız tanımı</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Dönem (20-21. yy)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Vivian Robson, Bernadette Brady ve diğer astrologlar
                      tarafından sistematik araştırmalar ve güncel kataloglar oluşturuldu.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Vivian Robson:</strong> "Fixed Stars and Constellations" (1923)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bernadette Brady:</strong> Parans sistemini geliştirdi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Modern yazılımlar:</strong> Otomatik hesaplama</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Fixed Stars */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Başlıca Sabit Yıldızlar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Regulus */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <Crown className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Regulus (Kalp Aslan)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">29° 57' Aslan • Mag: 1.35</p>
                    <p className="text-slate-300 text-sm font-medium">"Kraliyet Yıldızı"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">Liderlik ve otorite</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">Şöhret ve tanınma</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">Güneş-Mars doğası</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spica */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♠</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Spica (Başak)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">23° 57' Terazi • Mag: 0.97</p>
                    <p className="text-slate-300 text-sm font-medium">"Bereket Yıldızı"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Bilim ve teknoloji</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Sanatsal yetenek</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Venüs-Mars doğası</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Antares */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Antares (Akrep Kalbi)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">9° 54' Yay • Mag: 1.09</p>
                    <p className="text-slate-300 text-sm font-medium">"Mars'ın Rakibi"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Cesaret ve mücadele</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Yoğun duygular</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Mars-Jüpiter doğası</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aldebaran */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🐂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Aldebaran (Boğa Gözü)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">9° 54' İkizler • Mag: 0.85</p>
                    <p className="text-slate-300 text-sm font-medium">"Bekçi Yıldız"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Başarı ve tanınma</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Güç ve prestij</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Mars doğası</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sirius */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-white to-blue-300 rounded-full flex items-center justify-center">
                      <span className="text-slate-800 text-lg">🐕</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Sirius (Köpek Yıldızı)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">14° 12' Yengeç • Mag: -1.46</p>
                    <p className="text-slate-300 text-sm font-medium">"En Parlak Yıldız"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-200 rounded-full"></div>
                        <span className="text-slate-400">Sadakat ve bağlılık</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-200 rounded-full"></div>
                        <span className="text-slate-400">Ruhsal rehberlik</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-200 rounded-full"></div>
                        <span className="text-slate-400">Jüpiter-Mars doğası</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vega */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🎵</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Vega (Çalgı Yıldızı)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">15° 26' Oğlak • Mag: 0.03</p>
                    <p className="text-slate-300 text-sm font-medium">"Sanat ve Müzik"</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Müzikal yetenek</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Yaratıcı ifade</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Venüs-Merkür doğası</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Royal Stars */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kraliyet Yıldızları (Royal Stars)</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="mb-4">
                  <p className="text-slate-300">
                    Antik Perslerin "Dört Bekçi" veya "Kraliyet Yıldızları" olarak adlandırdığı
                    dört büyük yıldız, astrolojide özel bir öneme sahiptir.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-slate-750 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Crown className="h-5 w-5 text-yellow-400" />
                        <h4 className="font-semibold text-slate-100">Regulus - Kral</h4>
                      </div>
                      <div className="text-sm text-slate-400">
                        <div>Güney bekçisi • Aslan 29°</div>
                        <div>Liderlik, otorite, kraliyet onuru</div>
                      </div>
                    </div>

                    <div className="bg-slate-750 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Star className="h-5 w-5 text-blue-400" />
                        <h4 className="font-semibold text-slate-100">Fomalhaut - Bekçi</h4>
                      </div>
                      <div className="text-sm text-slate-400">
                        <div>Güney bekçisi • Balık 3°</div>
                        <div>Ruhsal güç, mistik bilgi</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-slate-750 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Zap className="h-5 w-5 text-orange-400" />
                        <h4 className="font-semibold text-slate-100">Aldebaran - Savaşçı</h4>
                      </div>
                      <div className="text-sm text-slate-400">
                        <div>Doğu bekçisi • İkizler 9°</div>
                        <div>Cesaret, başarı, liderlik</div>
                      </div>
                    </div>

                    <div className="bg-slate-750 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Eye className="h-5 w-5 text-red-400" />
                        <h4 className="font-semibold text-slate-100">Antares - Bekçi</h4>
                      </div>
                      <div className="text-sm text-slate-400">
                        <div>Batı bekçisi • Yay 9°</div>
                        <div>Güç, tutku, dönüşüm</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Methods */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Uygulama Yöntemleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-cyan-400" />
                    Conjunction Yöntemi
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      En yaygın kullanılan yöntem. Sabit yıldızın gezegen veya
                      açılarla conjunction yaptığı durumları inceler.
                    </p>
                    <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2">Orb Kuralları:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>• 1. magnitude yıldızlar: 2-3° orb</div>
                        <div>• 2-3. magnitude yıldızlar: 1-2° orb</div>
                        <div>• Luminaries (Güneş/Ay) için geniş orb</div>
                        <div>• Sadece conjunction aspecti kullanılır</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-purple-400" />
                    Paran Sistemi
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Bernadette Brady tarafından geliştirilen modern yöntem.
                      Yıldızın doğuş, batış ve meridyen geçişlerini inceler.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Heliacal Rising:</strong> Yıldızın ilk görünüşü</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Culmination:</strong> Meridyen geçişi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Setting:</strong> Batış zamanı</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interpretation Examples */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Örnekleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-yellow-300">Güneş conjunct Regulus</h3>
                  <div className="space-y-3">
                    <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                      <div className="font-medium text-yellow-200 mb-2">Pozitif Manifestasyon:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Doğal liderlik yetenekleri</div>
                        <div>• Otorite pozisyonlarında başarı</div>
                        <div>• Kraliyet benzeri onur ve prestij</div>
                        <div>• Güçlü karakter ve etkileme gücü</div>
                      </div>
                    </div>
                    <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4">
                      <div className="font-medium text-red-200 mb-2">Potansiyel Zorluklar:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Aşırı gurur ve ego problemleri</div>
                        <div>• Diktatör eğilimleri</div>
                        <div>• Başarıdan sonra ani düşüşler</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-blue-300">Venüs conjunct Spica</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Özel Yetenekler:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Olağanüstü sanatsal yetenek</div>
                        <div>• Bilimsel ve teknolojik başarı</div>
                        <div>• Estetik zevk ve yaratıcılık</div>
                        <div>• Toplumsal başarı ve tanınma</div>
                      </div>
                    </div>
                    <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4">
                      <div className="font-medium text-green-200 mb-2">Kariyer Alanları:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Sanat ve tasarım</div>
                        <div>• Mühendislik ve teknoloji</div>
                        <div>• Müzik ve performans sanatları</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Klasik Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Fixed Stars and Constellations in Astrology" - Vivian Robson</div>
                    <div>• "The Living Stars" - Eric Morse</div>
                    <div>• "Fixed Stars and Judicial Astrology" - George Noonan</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Brady's Book of Fixed Stars" - Bernadette Brady</div>
                    <div>• "The Fixed Star Health and Behavior Imbalance" - Ted George & Barbara Parker</div>
                    <div>• "Secrets of the Ancient Skies" - Diana Rosenberg</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Sabit yıldızlar astrolojinin en eski ve en kompleks dallarından birisidir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel fixed star analizi için deneyimli astrologlarla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/progressed"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Progressed Haritalar
              </Link>

              <Link
                href="/about/astrology/advanced-systems/arabic-parts"
                className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
              >
                Arap Parçaları
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}